import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

/**
 * Ground-parked detection (Flight Summary Patterns · Pattern 01).
 *
 * When Live Positions returns nothing for a tail, this hook falls back to
 * flight-summary on a ±72h/+24h window and identifies the most recent leg
 * that has already landed. The tail is then "parked" at that leg's arrival
 * airport (ADS-B transponder off on the ground is the usual reason Live
 * Positions goes empty on an aircraft that just landed).
 *
 * Returns:
 *   { status: 'PARKED', airport, landedAt, lastLeg }
 *   { status: 'NO_ACTIVITY' }       — no landed leg in the 4-day window
 *   { status: 'UNKNOWN' }           — still fetching or missing tail
 *
 * The window is deliberately narrow: 3 days back + 1 day forward. Widening
 * it invites older "was here last week" hits that don't mean the aircraft
 * is currently on the ground there.
 *
 * @param {string|null} tailNumber - Aircraft registration. Skip when null.
 * @param {Object}   [opts]
 * @param {boolean}  [opts.enabled=true] - Set to false to skip the fetch
 *   (e.g. Live Positions already returned data — no fallback needed).
 */
export function useGroundParkedStatus(tailNumber, { enabled = true } = {}) {
  const reg = tailNumber ? String(tailNumber).trim().toUpperCase() : null;

  const query = useQuery({
    queryKey: ['ground-parked', reg],
    queryFn: async () => {
      const now = Date.now();
      const from = new Date(now - 72 * 60 * 60 * 1000).toISOString();
      const to = new Date(now + 24 * 60 * 60 * 1000).toISOString();
      const raw = await apiClient.getFlightSummary({ registrations: reg, from, to });
      return Array.isArray(raw) ? raw : raw?.data || [];
    },
    enabled: !!reg && enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  if (!reg || query.isLoading || query.isError) {
    return { status: 'UNKNOWN', isLoading: query.isLoading, error: query.error };
  }

  const rows = query.data || [];
  const nowMs = Date.now();

  // Most recent leg whose datetime_landed is set and in the past.
  const landed = rows
    .filter((r) => {
      if ((r?.reg || '').trim().toUpperCase() !== reg) return false;
      const t = r?.datetime_landed ? new Date(r.datetime_landed).getTime() : NaN;
      return Number.isFinite(t) && t <= nowMs;
    })
    .sort(
      (a, b) => new Date(b.datetime_landed).getTime() - new Date(a.datetime_landed).getTime()
    )[0];

  if (!landed) {
    return { status: 'NO_ACTIVITY', isLoading: false };
  }

  // Prefer actual destination when the leg was diverted.
  const airport = (
    landed.dest_icao_actual ||
    landed.dest_icao ||
    landed.dest_iata ||
    ''
  ).toUpperCase();

  return {
    status: 'PARKED',
    airport,
    landedAt: landed.datetime_landed,
    lastLeg: landed,
    isLoading: false,
  };
}

/**
 * Small helper for "landed 2h ago" strings. Kept alongside the hook so any
 * consumer surfacing the status renders the same phrasing.
 */
export function formatLandedAgo(landedAtIso) {
  if (!landedAtIso) return null;
  const ms = Date.now() - new Date(landedAtIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}
